import { ColorPicker, ColorPickerProps, GetProp } from 'antd';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@desygner/ui-common-translation';

import { useTheme } from '../../providers';
import { Button, Icon } from '../../ui';
import { debounce, throttle } from '../../utils';
import { Tooltip } from '../Tooltip';
import {
  BulletSize,
  BulletStyle,
  StyledColorButton,
  StyledIconWrapper,
  StyledInputColor,
  StyledPanelWrapper,
  StyledWrapper,
} from './ColorBullet.style';

export interface ColorBulletProps {
  color: string | 'mixed';
  variant?: BulletStyle;
  size?: BulletSize;
  readOnly?: boolean;
  isPicker?: boolean;
  isSelected?: boolean;
  title?: string;
  placement?: GetProp<ColorPickerProps, 'placement'>;
  onChange?: (color: string, additionalData?: ColorBulletProps['additionalData']) => void;
  onChangeComplete?: (color: string) => void;
  onApply?: (color: string) => void;
  onRemove?: (color: string) => void;
  additionalData?: {
    smartAsset?: string;
    colorGroup?: string;
  };
  alpha?: boolean;
}

export interface ColorIconProps {
  color: ColorBulletProps['color'];
  variant?: ColorBulletProps['variant'];
  size?: ColorBulletProps['size'];
  isPicker?: ColorBulletProps['isPicker'];
  isSelected?: ColorBulletProps['isSelected'];
  readOnly?: ColorBulletProps['readOnly'];
  title?: ColorBulletProps['title'];
  isOpened?: boolean;
  onClick?: () => void;
}
export const ColorIcon = forwardRef<HTMLDivElement, ColorIconProps>(function ColorIcon(
  {
    variant = 'circle',
    size = 'Medium',
    isPicker = false,
    isSelected = false,
    readOnly,
    color,
    isOpened,
    onClick,
  },
  ref
) {
  const theme = useTheme();

  const iconSize = useMemo(() => {
    switch (size) {
      case 'Small':
        return 'XXSmall';
      case 'Medium':
        return 'XSmall';
      case 'Large':
        return 'Small';
      default:
        return 'XSmall';
    }
  }, [size]);

  const defaultValue = useMemo(
    function removeAlphaFromHex() {
      if (typeof color !== 'string' || !color.startsWith('#')) {
        return color;
      }

      return color.slice(0, 7);
    },
    [color]
  );

  return (
    <StyledColorButton
      ref={ref}
      className="color-bullet"
      $variant={variant as BulletStyle}
      $size={size as BulletSize}
      $isPicker={isPicker}
      $isSelected={isSelected}
      $isReadOnly={readOnly}
      $color={color}
      onClick={onClick}
    >
      <StyledInputColor type="color" $isReadOnly={true} {...(defaultValue && { defaultValue })} />

      {isPicker && (
        <Icon
          name="add-custom-list---16"
          size={iconSize}
          angle={isOpened ? 45 : 0}
          color={theme.Color.Neutrals.Dark}
        />
      )}
    </StyledColorButton>
  );
});
export function ColorBullet({
  color,
  additionalData,
  variant = 'circle',
  size = 'Medium',
  isPicker = false,
  isSelected = false,
  readOnly,
  title,
  placement,
  alpha = true,
  onChange,
  onChangeComplete,
  onApply,
  onRemove,
}: ColorBulletProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedColor, setSelectedColor] = useState(
    !color || isPicker || color === 'picker' ? '#000000' : color
  );
  const [isOpened, setIsOpened] = useState(false);
  const colorIconRef = useRef<HTMLDivElement>(null);

  const hexColor = useMemo(() => {
    if (color === 'transparent' || !color) {
      return t('general.transparent');
    }

    return color.toLocaleUpperCase();
  }, [color, t]);

  useEffect(
    function updateColorWhenPropChanges() {
      setSelectedColor(!color || isPicker || color === 'picker' ? '#000000' : color);
    },
    [color, isPicker]
  );

  const handleChange = useCallback(
    throttle((newColor: string) => {
      if (readOnly) {
        onChange?.(color, additionalData);
        return;
      }

      onChange?.(newColor, additionalData);
      setSelectedColor(newColor);
    }, 250),
    [onChange, readOnly, color, additionalData]
  );

  const handleChangeComplete = useCallback(
    debounce((newColor: string) => {
      /**
       * ReadOnly means that the color won't open a color picker and will only show the color
       * This needs to be refactored to use `isPicker` instead of `readOnly`
       */
      onChangeComplete?.(newColor);

      if (!readOnly) {
        setSelectedColor(newColor);
      }
    }, 500),
    [onChangeComplete, readOnly]
  );

  const handleClick = useCallback(
    (selectedColor: string) => {
      if (readOnly) {
        handleChange(selectedColor);
        handleChangeComplete(selectedColor);
        return;
      }
    },
    [handleChange, handleChangeComplete, readOnly]
  );

  return (
    <Tooltip text={title || hexColor} visible={!isPicker}>
      <StyledWrapper className="color-bullet-wrapper">
        {onRemove && !isPicker && (
          <StyledIconWrapper onClick={() => onRemove(selectedColor)}>
            <Icon name={'cross'} size={'XXSmall'} color={theme.Color.NeutralsFixed.Paper} />
          </StyledIconWrapper>
        )}
        <ColorPicker
          disabledAlpha={!alpha}
          defaultFormat="hex"
          defaultValue={selectedColor}
          disabled={readOnly}
          onChange={(value) => {
            handleChange(value.toHexString());
          }}
          onChangeComplete={(value) => {
            handleChangeComplete(value.toHexString());
          }}
          onOpenChange={(open) => {
            setIsOpened(open);
          }}
          panelRender={(panel) => {
            return (
              <StyledPanelWrapper>
                {panel}
                {onApply && (
                  <Button onClick={() => onApply(selectedColor)} size={'Small'}>
                    {t('general.apply')}
                  </Button>
                )}
              </StyledPanelWrapper>
            );
          }}
          placement={placement ? placement : 'bottomLeft'}
        >
          <ColorIcon
            ref={colorIconRef}
            variant={variant}
            size={size}
            isPicker={isPicker}
            isSelected={isSelected}
            readOnly={readOnly}
            title={title}
            color={selectedColor}
            onClick={() => {
              handleClick(selectedColor);
            }}
            isOpened={isOpened}
          />
        </ColorPicker>
      </StyledWrapper>
    </Tooltip>
  );
}
